import { isAxiosError } from 'axios'

import type { ApiClient } from 'clients/yogi-api'
import type {
  CatalogPageState,
  CatalogPageStateRead,
  SourceCustomFieldAdd,
  SourceCustomFieldDelete,
  SourceDetailsVariables,
  SourceGroup,
  SourcesInfo,
} from 'features/catalog/types'
import type { FieldCatalogResponse } from 'features/charts-v2/types/yogiql'
import { checkYogiQLVersion } from 'features/charts-v2/utils'
import type {
  StatsComparisonBody,
  StatsComparisonResponse,
} from 'features/summary'
const STATS_URL = '/client/catalog/stats'
const PROJECT_DETAILS_URL = '/project'
const PROJECT_SOURCE_GROUP_URL = '/project/sources/group'
const PROJECT_SOURCE_GROUP_INFO_URL = '/project/sources/group/info'
const SOURCE_UPDATE_URL = '/source/update'
const SOURCE_CUSTOMFIELD_DELETE_URL = '/source/field'
const POST_ADD_CUSTOM_FIELD = '/post/field/value'
const HASH_FILTER_URL = '/project/hash-filter'
const YOGIQL_PAGE_STATE_URL = '/client/catalog/yogiql/page-state'
const FIELD_CATALOG_URL = '/client/field-catalog'
const HUB_UUID_HEADER = 'X-Hub-UUID'
import type { ProjectDetails } from 'types/project'

export class CatalogService {
  constructor(private client: ApiClient) {}

  /**
   * Gets the source group info within the name (Ex. Amazon)
   * @param key
   * @param sourcename name of the source
   * @param syndicated_source whether or not syndicated sources
   * @param proj_uuid uuid of project
   */
  async getSourceGroupInfo(
    _key: string,
    sourcename: string,
    syndicated_source: boolean,
    proj_uuid: string,
  ): Promise<SourcesInfo> {
    const { data } = await this.client.get<SourcesInfo>(
      PROJECT_SOURCE_GROUP_INFO_URL,
      {
        params: {
          proj_uuid: proj_uuid,
          source_name: sourcename,
          syndicated_sources: syndicated_source,
        },
      },
    )
    return data
  }

  /**
   * Gets the Source Groups
   * @param key
   * @param syndicated_sources gets the syndicated sources
   * @param proj_uuid gets the proj_uuid
   * @param breakdown_by_country when true each row is a (source, country) pair
   * @param dateMin epoch seconds lower bound for post create_time (inclusive)
   * @param dateMax epoch seconds upper bound for post create_time (inclusive)
   */
  async getSourceGroups(
    _key: string,
    syndicated_sources: boolean,
    proj_uuid: string,
    breakdown_by_country?: boolean,
    dateMin?: number,
    dateMax?: number,
  ): Promise<SourceGroup[]> {
    const { data } = await this.client.get<SourceGroup[]>(
      PROJECT_SOURCE_GROUP_URL,
      {
        params: {
          proj_uuid: proj_uuid,
          syndicated_sources: syndicated_sources,
          ...(breakdown_by_country ? { breakdown_by_country: true } : {}),
          date_min: dateMin,
          date_max: dateMax,
        },
      },
    )
    return data
  }

  /**
   * Fetches project details
   * @param {string} proj_uuid - The project UUID
   * @returns {Promise<ProjectDetails>} List of projects
   */
  async getProjectDetails(
    _key: string,
    proj_uuid: string,
  ): Promise<ProjectDetails> {
    const { data } = await this.client.get<ProjectDetails>(
      PROJECT_DETAILS_URL,
      {
        params: { proj_uuid },
      },
    )
    return data
  }

  /**
   * Fetches project details
   * @param {string} proj_uuid - The project UUID
   * @returns {Promise<SourceDetails>} List of projects
   */
  async updateSource({
    uuid,
    title,
    description,
  }: SourceDetailsVariables): Promise<unknown> {
    const { data } = await this.client.put(
      SOURCE_UPDATE_URL,
      { title, description },
      { params: { uuid } },
    )
    return data
  }

  /**
   * Fetches project details
   * @param {string} proj_uuid - The project UUID
   * @returns {Promise<SourceDetails>} List of projects
   */
  async deleteSource({
    uuids,
    fields,
  }: SourceCustomFieldDelete): Promise<unknown> {
    const { data } = await this.client.delete(SOURCE_CUSTOMFIELD_DELETE_URL, {
      data: { uuids, fields },
    })
    return data
  }

  /**
   * Add Custom Field
   *
   */
  async addCustomField({
    resource_uuid,
    field,
    resource,
    field_value,
    field_type,
    lookup_field,
    lookup_type,
    add_field,
  }: SourceCustomFieldAdd): Promise<unknown> {
    const { data } = await this.client.post(
      POST_ADD_CUSTOM_FIELD,
      {
        resource_uuid,
        field,
        resource,
        field_value,
        field_type,
        lookup_field,
        lookup_type,
        add_field,
      },
      { params: { resource_uuid } },
    )
    return data
  }

  /**
   * The detail Overview's stats card: current-period stats for the item, and
   * the prior period's when `prev_panel` is given.
   * @param proj_uuid uuid of project
   * @param body the item's panel, its prior-period panel, and the record type
   */
  async getStatsComparison(
    proj_uuid: string,
    body: StatsComparisonBody,
  ): Promise<StatsComparisonResponse> {
    const { data } = await this.client.post<StatsComparisonResponse>(
      STATS_URL,
      body,
      { params: { proj_uuid } },
    )
    return data
  }

  /**
   * Saves the catalog filter panel's state and returns its content-addressed
   * hash — the same `/project/hash-filter` endpoint Feedback/Keywords/charts-v2
   * use for their own filter-hash persistence (see
   * `features/feedback/service/state-service.ts`).
   * @param proj_uuid uuid of project
   * @param payload the page name plus the JSON-stringified filter body
   */
  async saveFilterState(
    proj_uuid: string,
    payload: { page: string; filterString: string },
  ): Promise<string> {
    const { data } = await this.client.post<string>(HASH_FILTER_URL, payload, {
      params: { proj_uuid },
    })
    return data
  }

  /** Stores the state under `enhanced_dialect`; themes and datasets by name. */
  saveYogiQL(proj_uuid: string, state: CatalogPageState): Promise<string> {
    return this.client
      .post<{ key: string }>(
        YOGIQL_PAGE_STATE_URL,
        { state },
        { headers: { [HUB_UUID_HEADER]: proj_uuid } },
      )
      .then(({ data }) => data.key)
  }

  /** Reads any key, on every hub; an unknown or another hub's key is `not_found`. */
  async loadYogiQL(
    proj_uuid: string,
    key: string,
  ): Promise<CatalogPageStateRead> {
    try {
      const { data } = await this.client.get<CatalogPageStateRead>(
        `${YOGIQL_PAGE_STATE_URL}/${encodeURIComponent(key)}`,
        { headers: { [HUB_UUID_HEADER]: proj_uuid } },
      )
      return data
    } catch (error: unknown) {
      if (isAxiosError(error) && error.response?.status === 404) {
        return { format: 'not_found' }
      }
      throw error
    }
  }

  /** The hub's YogiQL field catalog, the flagged filters encode against. */
  getFieldCatalog(proj_uuid: string): Promise<FieldCatalogResponse> {
    return this.client
      .get<FieldCatalogResponse>(FIELD_CATALOG_URL, {
        headers: { [HUB_UUID_HEADER]: proj_uuid },
      })
      .then(({ data }) => checkYogiQLVersion(data))
  }
}
